import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import type { GeoPackageContent } from '../data/import/geopackageCore';
import type { GeoPackageSession } from '../data/import/geopackageImport';
import {
  formatGeoPackageApproximateBytes,
  GEOPACKAGE_PREFLIGHT_LABELS,
  isGeoPackageContentReady,
  summarizeGeoPackageSelection,
} from '../data/import/geopackagePreflight';
import { DialogShell } from './DialogShell';

interface Props {
  readonly session: GeoPackageSession;
  readonly working: boolean;
  readonly onClose: () => void;
  readonly onImport: (names: readonly string[]) => void;
  readonly error?: string | null;
  readonly returnFocusTo?: HTMLElement | null;
}

function contentType(content: GeoPackageContent): string {
  if (content.kind === 'table') return 'Table';
  if (content.kind === 'vector') {
    const type = content.geometryType ?? 'Geometry';
    return type
      .replace(/^MULTI/i, 'Multi')
      .replace(/POLYGON/i, 'Polygon')
      .replace(/LINESTRING/i, 'LineString')
      .replace(/POINT/i, 'Point');
  }
  if (content.detail.startsWith('Raster tiles')) return 'Raster tiles';
  return 'Unsupported';
}

function contentCrs(content: GeoPackageContent): string {
  if (content.kind !== 'vector') return '—';
  if (content.epsgCode) return `EPSG:${content.epsgCode}`;
  if (content.crsDefinition) return 'Custom CRS';
  return content.srsId && content.srsId > 0 ? `SRS ${content.srsId}` : '—';
}

export function GeoPackageContentsDialog({
  session,
  working,
  onClose,
  onImport,
  error,
  returnFocusTo,
}: Props) {
  const titleId = useId();
  const preflight = useSyncExternalStore(
    session.subscribePreflight,
    session.getPreflightSnapshot,
    session.getPreflightSnapshot,
  );
  const touched = useRef(false);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const contents = session.discovery.contents;
  const ready = useMemo(
    () => contents.filter((content) => isGeoPackageContentReady(content, preflight)),
    [contents, preflight],
  );
  const summary = summarizeGeoPackageSelection(contents, preflight, selected);

  useEffect(() => {
    setSelected((current) =>
      touched.current
        ? current.filter((name) => ready.some((content) => content.tableName === name))
        : ready.map((content) => content.tableName),
    );
  }, [ready]);

  return (
    <DialogShell
      className="new-map-dialog geopackage-dialog"
      onClose={onClose}
      returnFocusTo={returnFocusTo}
      titleId={titleId}
    >
      <p className="eyebrow">Local data import</p>
      <h2 id={titleId}>Choose GeoPackage contents</h2>
      <p className="geopackage-intro">Choose the layers and tables to add to this map.</p>
      <div className="geopackage-select-actions">
        <button
          disabled={working || ready.length === 0 || selected.length === ready.length}
          onClick={() => {
            touched.current = true;
            setSelected(ready.map((content) => content.tableName));
          }}
          type="button"
        >
          Select all
        </button>
        <button
          disabled={working || selected.length === 0}
          onClick={() => {
            touched.current = true;
            setSelected([]);
          }}
          type="button"
        >
          Clear all
        </button>
      </div>
      <div className="geopackage-content-list" role="region" aria-label="GeoPackage contents">
        <table className="geopackage-contents-table">
          <thead>
            <tr>
              <th scope="col" aria-label="Select" />
              <th scope="col">Name</th>
              <th scope="col">Type</th>
              <th scope="col">CRS</th>
              <th scope="col">Records</th>
              <th scope="col">Approx. size</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {contents.map((content) => {
              const value = preflight[content.tableName];
              const importable = isGeoPackageContentReady(content, preflight);
              const status =
                content.kind === 'unsupported'
                  ? 'Not supported'
                  : value
                    ? GEOPACKAGE_PREFLIGHT_LABELS[value.status]
                    : 'Checking…';
              return (
                <tr
                  className={importable ? undefined : 'geopackage-unavailable'}
                  key={content.tableName}
                >
                  <td>
                    <input
                      type="checkbox"
                      aria-label={content.title}
                      checked={importable && selected.includes(content.tableName)}
                      disabled={working || !importable}
                      onChange={(event) => {
                        touched.current = true;
                        setSelected((current) =>
                          event.target.checked
                            ? [...current, content.tableName]
                            : current.filter((name) => name !== content.tableName),
                        );
                      }}
                    />
                  </td>
                  <td className="geopackage-name-cell" title={content.title}>
                    <span className="geopackage-name">{content.title}</span>
                    <small className="geopackage-mobile-detail">
                      {contentType(content)} · {contentCrs(content)}
                      {value ? ` · ${value.recordCount.toLocaleString()} records` : ''}
                    </small>
                  </td>
                  <td className="geopackage-type-cell">{contentType(content)}</td>
                  <td className="geopackage-crs-cell">{contentCrs(content)}</td>
                  <td className="geopackage-records-cell">
                    {value && content.kind !== 'unsupported' && value.status !== 'check-failed'
                      ? value.recordCount.toLocaleString()
                      : '—'}
                  </td>
                  <td className="geopackage-size-cell">
                    {value &&
                    content.kind !== 'unsupported' &&
                    value.status !== 'check-failed' &&
                    value.status !== 'too-many-fields'
                      ? formatGeoPackageApproximateBytes(value.approximateBytes)
                      : '—'}
                  </td>
                  <td className="geopackage-status-cell">{status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="geopackage-selection-summary" role="status">
        <span>
          Selected: {summary.contents.toLocaleString()} contents ·{' '}
          {summary.records.toLocaleString()} records ·{' '}
          {formatGeoPackageApproximateBytes(summary.approximateBytes)} estimated
        </span>
        {summary.checking ? <span>Checking {summary.checking} contents…</span> : null}
      </div>
      {summary.issue ? (
        <p className="import-error geopackage-selection-issue">{summary.issue}</p>
      ) : null}
      {error ? (
        <p className="import-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="new-map-dialog-actions">
        <button disabled={working} onClick={onClose} type="button">
          Cancel
        </button>
        <button
          className="primary-action"
          disabled={
            working || summary.checking > 0 || selected.length === 0 || Boolean(summary.issue)
          }
          onClick={() => onImport(selected)}
          type="button"
        >
          Import selected
        </button>
      </div>
    </DialogShell>
  );
}
